import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReportSubstantiation } from 'src/api';
import {
  LocalFilterOptions,
  ResetFilterOptions,
  SaveFilterParams,
} from 'src/components/organisms';
import {
  TimeframeInput,
  useGetSubstantiationStatusCountQuery,
} from 'src/graphql';
import { InsightsDashboard, LocationFilterCategory, State } from 'src/types';
import {
  chartColors,
  roundPercentages,
  sortByDefaultOrder,
  sortDataArrayByNumber,
  sortDataArrayByPercentage,
} from 'src/utils';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';
import { getYear } from 'src/utils/time';
import { getColumnNames } from '../helpers';
import { ColumnSortType, TableDataType } from './useCasesOverTime';

interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
  organisationData: {
    sites?: string[];
    countries?: string[];
    departments?: string[];
    oldestReportSubmittedAtDate: Date;
    categories?: string[];
  };
}

const tableColumns = [
  { label: 'Substantiation status' },
  { label: 'Total reports' },
  { label: 'Overall percentage' },
];

export const useSubstantiationTotals = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
  organisationData,
}: Parameters) => {
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.OPERATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [selectedDepartments, setSelectedDepartments] = useState(
    organisationData.departments ?? [],
  );
  const [selectedCategories, setSelectedCategories] = useState(
    organisationData.categories ?? [],
  );
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [localLocationFilter, setLocalLocationFilter] = useState(
    currentGlobalLocationFilter,
  );
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [hoverSegment, setHoverSegment] = useState<number | undefined>();
  const [displayTableData, setDisplayTableData] = useState<TableDataType[]>([]);
  const [storedTableData, setStoredTableData] = useState<TableDataType[]>([]);
  const [sortedColumn, setSortedColumn] = useState<ColumnSortType>({
    columnName: 'Substantiation status',
    direction: 'asc',
  });
  const [pdfIsDownloading, setPdfIsDownloading] = useState<boolean>(false);
  const substantiationStatuses = Object.values(ReportSubstantiation);
  const substantiationLegend = substantiationStatuses.map((status, i) => {
    return { text: status, color: chartColors[i], active: true };
  });

  useEffect(() => {
    setSelectedSites(globalFilterSites);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterSites, currentGlobalLocationFilter]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterCountries, currentGlobalLocationFilter]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  useEffect(() => {
    if (!selectedDepartments.length && organisationData.departments?.length) {
      setSelectedDepartments(organisationData.departments);
    }
  }, [organisationData.departments, selectedDepartments.length]);

  useEffect(() => {
    if (!selectedCategories.length && organisationData.categories?.length) {
      setSelectedCategories(organisationData.categories);
    }
  }, [organisationData.categories, selectedCategories.length]);

  const queryLocationVariables = buildLocationQueryVariables({
    locationFilter: localLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountryCodes,
    caseRegionIds: selectedCaseRegionIds,
  });

  const { data } = useGetSubstantiationStatusCountQuery({
    timeframe,
    ...queryLocationVariables,
    departments: selectedDepartments,
    categories: selectedCategories,
  });
  const barHorizontalData = data?.getSubstantiationStatusCount;
  const total = barHorizontalData?.data.reduce(
    (prev, current) => prev + current.value,
    0,
  );
  const percentageValues = roundPercentages(
    barHorizontalData
      ? barHorizontalData.data.map((data) =>
          total ? (data.value / total) * 100 : 0,
        )
      : [],
  );
  const barHorizontalDataArray =
    barHorizontalData?.data.map((p, index) => ({
      label: p.label,
      value: p.value,
      bar: p.bar,
      percentage: percentageValues[index],
    })) ?? [];
  const isCaseMode =
    storedGlobalFilters.locationFilterCategory === LocationFilterCategory.CASE;

  const localFilterOptions: LocalFilterOptions = {
    ...(isCaseMode
      ? {}
      : {
          site: {
            options: organisationData.sites ?? [],
            selectedOptions: selectedSites,
          },
          country: {
            options: organisationData.countries ?? [],
            selectedOptions: selectedCountries,
          },
          selectedLocationFilter: localLocationFilter,
        }),
    time: {
      timeframe,
      oldestSelectableYear: getYear(
        organisationData.oldestReportSubmittedAtDate,
      ),
    },
    department: {
      options: organisationData.departments ?? [],
      selectedOptions: selectedDepartments,
    },
    category: {
      options: organisationData.categories ?? [],
      selectedOptions: selectedCategories,
    },
  };
  const saveLocalFilters = (params: SaveFilterParams) => {
    if (params.selectedSites) {
      setSelectedSites(params.selectedSites);
    }
    if (params.selectedCountries) {
      setSelectedCountries(params.selectedCountries);
    }
    if (params.selectedDepartments) {
      setSelectedDepartments(params.selectedDepartments);
    }
    if (params.selectedTimeframe) {
      setTimeframe(params.selectedTimeframe);
    }
    if (params.selectedCategories) {
      setSelectedCategories(params.selectedCategories);
    }
    if (params.selectedLocationFilter) {
      setLocalLocationFilter(params.selectedLocationFilter);
    }
  };

  useEffect(() => {
    const tableData =
      barHorizontalData?.data.map((data, index) => {
        return {
          cellData: [data.label, data.value, `${percentageValues[index]}%`],
          row: data.label,
        };
      }) ?? [];

    if (!isEqual(tableData, storedTableData)) {
      setStoredTableData(tableData);
    }
  }, [barHorizontalData?.data, data, percentageValues, storedTableData]);

  const handleColumnSort = useCallback(
    (columnName: string, direction: 'asc' | 'desc') => {
      const columnIndex = getColumnNames(tableColumns).indexOf(columnName);
      setSortedColumn({ columnName, direction });
      switch (columnName) {
        case 'Substantiation status': {
          const newTableData = sortByDefaultOrder(storedTableData, direction);
          setDisplayTableData(newTableData);
          break;
        }
        case 'Total reports': {
          const newTableData = sortDataArrayByNumber(
            storedTableData,
            columnIndex,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        case 'Overall percentage': {
          const newTableData = sortDataArrayByPercentage(
            storedTableData,
            columnIndex,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        default: {
          setDisplayTableData(storedTableData);
          break;
        }
      }
    },
    [storedTableData],
  );

  useEffect(() => {
    handleColumnSort(sortedColumn.columnName, sortedColumn.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedColumn.columnName, sortedColumn.direction, storedTableData]);

  const resetLocalFilters: ResetFilterOptions = {
    siteOptions: globalFilterSites,
    countryOptions: globalFilterCountries,
    departmentOptions: organisationData.departments ?? [],
    timeframe: globalFilterTimeframe,
    categoryOptions: organisationData.categories ?? [],
    globalFilterLocation: currentGlobalLocationFilter,
  };

  return {
    xTitle: barHorizontalData?.xTitle,
    yTitle: barHorizontalData?.yTitle,
    data: barHorizontalDataArray,
    substantiationLegend,
    onHover: (d?: { bar: number }) => setHoverSegment(d?.bar),
    substantiationHoverBarColor: hoverSegment
      ? chartColors[hoverSegment - 1]
      : undefined,
    localFilterOptions,
    saveLocalFilters,
    tableColumns,
    tableData: displayTableData,
    handleColumnSort,
    sortedColumn,
    pdfIsDownloading,
    setPdfIsDownloading,
    localLocationFilter,
    resetLocalFilters,
  };
};
